import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Countries from 'components/addresses/countries'
import { SHIPPING_COUNTRY_CHANGED_EVENT } from 'components/addresses/countries/const'
import CountrySelectDrawer from 'components/header/country-select-drawer/CountrySelectDrawer'
import { configs } from 'config/shop-language.json'
import CountryApiHandler from 'lib/api/country'
import StateStorage from 'lib/state-storage'

const normalizeCountryId = (value) => {
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const id = String(value)
  return /^[1-9]\d*$/.test(id) ? id : null
}

export const resolveDeliveryDestination = (
  shop,
  selectedId,
  countries = [],
) => {
  const market = configs.find(({ slug }) => slug === shop && slug !== 'global')
  const countryId =
    shop === 'global'
      ? normalizeCountryId(selectedId)
      : normalizeCountryId(market?.id_country)
  const destination = countryId
    ? configs.find(
        ({ slug, id_country }) =>
          slug !== 'global' && String(id_country) === countryId,
      )
    : null
  const country = countries.find(
    (item) => String(item?.id_country) === countryId,
  )

  return {
    countryId,
    countrySlug: destination?.slug || null,
    countryName:
      typeof country?.name === 'string' && country.name.trim()
        ? country.name.trim()
        : null,
  }
}

const readSelectedCountry = () => {
  try {
    return normalizeCountryId(StateStorage.getCountry())
  } catch (error) {
    return null
  }
}

const readCountriesCache = () => {
  try {
    const cached = StateStorage.getLocalState('pomeloCountries')
    return Array.isArray(cached?.countries) ? cached.countries : null
  } catch (error) {
    return null
  }
}

export const useDeliveryDestination = (shop) => {
  // Read browser storage after hydration; a global shop's configured US ID is
  // a currency/routing default, not evidence of a shipping destination.
  const [selection, setSelection] = useState(null)
  const requestId = useRef(0)
  const countriesRequest = useRef(null)
  const mounted = useRef(false)

  const refreshDestination = useCallback(() => {
    if (!mounted.current) return
    requestId.current += 1
    const currentRequest = requestId.current
    const selectedId = shop === 'global' ? readSelectedCountry() : null
    const countries = shop === 'global' ? readCountriesCache() : null
    setSelection({
      shop,
      ...resolveDeliveryDestination(shop, selectedId, countries || []),
    })

    if (shop !== 'global' || !selectedId || countries) return

    if (!countriesRequest.current) {
      countriesRequest.current = CountryApiHandler.getCountry()
        .catch(() => null)
        .then((response) => {
          countriesRequest.current = null
          return response
        })
    }

    countriesRequest.current.then((response) => {
      if (
        !mounted.current ||
        currentRequest !== requestId.current ||
        readSelectedCountry() !== selectedId
      ) {
        return
      }
      setSelection({
        shop,
        ...resolveDeliveryDestination(
          shop,
          selectedId,
          Array.isArray(response?.countries) ? response.countries : [],
        ),
      })
    })
  }, [shop])

  useEffect(() => {
    mounted.current = true
    refreshDestination()
    window.addEventListener(SHIPPING_COUNTRY_CHANGED_EVENT, refreshDestination)
    window.addEventListener('focus', refreshDestination)
    window.addEventListener('pageshow', refreshDestination)
    return () => {
      mounted.current = false
      requestId.current += 1
      window.removeEventListener(
        SHIPPING_COUNTRY_CHANGED_EVENT,
        refreshDestination,
      )
      window.removeEventListener('focus', refreshDestination)
      window.removeEventListener('pageshow', refreshDestination)
    }
  }, [refreshDestination])

  const destination =
    shop === 'global' && selection?.shop === shop
      ? selection
      : resolveDeliveryDestination(shop, null)

  return {
    countryId: destination.countryId,
    countrySlug: destination.countrySlug,
    countryName: destination.countryName,
    refreshDestination,
  }
}

const DestinationSelector = ({
  shop,
  label,
  marketLabel,
  onDestinationChange,
}) => {
  const [isMarketOpen, setMarketOpen] = useState(false)
  const [isCountryOpen, setCountryOpen] = useState(false)

  useEffect(() => {
    setMarketOpen(false)
    setCountryOpen(false)
  }, [shop])

  useEffect(() => {
    if (!onDestinationChange) return undefined
    window.addEventListener(SHIPPING_COUNTRY_CHANGED_EVENT, onDestinationChange)
    return () => {
      window.removeEventListener(
        SHIPPING_COUNTRY_CHANGED_EVENT,
        onDestinationChange,
      )
    }
  }, [onDestinationChange])

  return (
    <React.Fragment>
      <button
        type="button"
        className="delivery-policy__change-destination"
        onClick={() =>
          shop === 'global' ? setCountryOpen(true) : setMarketOpen(true)
        }
      >
        {label}
      </button>
      {shop === 'global' && (
        <button
          type="button"
          className="delivery-policy__change-market"
          onClick={() => setMarketOpen(true)}
        >
          {marketLabel}
        </button>
      )}
      {isMarketOpen && (
        <CountrySelectDrawer
          isOpenDrawer
          countryDrawerTrigger={<span aria-hidden="true" />}
          onCloseDrawer={() => setMarketOpen(false)}
        />
      )}
      {isCountryOpen && shop === 'global' && (
        <Countries isOpen onClose={() => setCountryOpen(false)} />
      )}
    </React.Fragment>
  )
}

DestinationSelector.defaultProps = {
  marketLabel: 'Change country / region',
  onDestinationChange: null,
}

DestinationSelector.propTypes = {
  shop: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  marketLabel: PropTypes.string,
  onDestinationChange: PropTypes.func,
}

export default DestinationSelector
